/* eg-rcia-arbre.js : arbre des recettes de la page Recettes (lot L2 ; R-plan § 2.3 à § 2.7, R-decisions points 10 et 11,
   amendements R-L0 A7 et A8). Module ES chargé par sections/eg-recettes.liquid.
   - Exporte choisir() et le vocabulaire, testés sous Node (outils/recettes/tests-js/arbre.test.mjs) ; le DOM n'est touché
     que si document existe.
   - Aucun ajout au panier ; une seule requête réseau : l'envoi de la boîte à idées (fonction « idee »), sur clic. Les textes venus de l'index sont posés par textContent (R12).
   - Stockage : localStorage « eg-arbre » = {e, n, d} seulement ; sessionStorage « eg-rcia-passage » écrit au clic sur une
     recette (lu puis supprimé par la fiche) ; état de l'historique de l'onglet (history.state) pour « Retour » et
     « Retour aux idées » : {e, n, d}, l'écran et les handles des idées montrées avec la signature de leurs contraintes.
     Rien de l'écran « Autre chose ? » (régimes, budget, « J'ai déjà ») n'est écrit ailleurs que dans le passage
     (R-decisions, « Toujours valables ») : les pastilles ne vivent que dans la mémoire de la page. */

export const ENVIES = ['pates', 'pizza_focaccia', 'risotto', 'apero', 'dessert'];
export const SURPRISE = 'surprise';
export const TAILLES = { t2: 2, t4: 4, t6: 6, t8: 8 };
export const DUREES = ['express', 'normal', 'long'];
export const CONTRAINTES_DURES = ['vegetarien', 'sans_porc', 'sans_gluten', 'sans_lactose', 'enfants'];
export const SOUPLES = ['petit_budget', 'recevoir'];
export const CONTRAINTES_ORDRE = ['vegetarien', 'sans_porc', 'sans_gluten', 'sans_lactose', 'enfants', 'petit_budget'];
export const DEJA = ['oeufs', 'parmesan', 'tomates', 'courgettes', 'champignons', 'creme', 'lardons', 'poulet', 'thon'];
export const N_MIN = 2;
export const PAR_PAGE = 3;
export const PORTIONS_BASE = 4;
export const CLE_LOCAL = 'eg-arbre';
export const CLE_PASSAGE = 'eg-rcia-passage';

const RE_H = /^[a-z0-9-]{1,80}$/;
const RE_U = /^\/(?!\/)[A-Za-z0-9/._~%-]*$/; // chemin du site uniquement : jamais un autre domaine ni « javascript: »
const RE_I = /^(https:)?\/\/[^\s"'<>\\]+$/;
const LEGENDES = ['reelle', 'ia', 'produit'];
const NIVEAUX = ['tres_facile', 'facile', 'intermediaire'];

export function rang(d) { return DUREES.indexOf(d); }

/* ------------------------------------------------------------------ Boîte à idées (16/10/2026)
   « Une envie que nous n'avons pas encore ? » : le texte part à la fonction Supabase « idee », qui l'anonymise et
   l'enregistre pour l'équipe ITALAZA (elle développe de nouvelles recettes à partir des envies reçues ; aucune IA).
   Ses mots servent aussi, dans la page seulement, à classer les recettes prêtes. Le texte n'est écrit ni dans
   l'historique, ni dans le stockage, ni dans le passage vers la fiche. */
export const TEXTE_MIN = 3;
export const TEXTE_MAX = 300;
export const CLE_CONV = 'eg-rcia-conv';
// Mots trop courants pour classer une recette (français, minuscules sans accents).
const VIDES = new Set(('avec sans pour une des les aux pas que qui est son ses mon mes vos votre nos notre leur plat plats '
  + 'recette recettes envie envies faire fait idee idees chose quelque bien tres plus peu moins comme dans sur par chez '
  + 'aime aimerais voudrais voulez veux bon bonne bons bonnes petit petite grand grande vraie vrai style facon maison').split(' '));

/** Minuscules, sans accents ni ligatures. */
export function normaliser(t) {
  return String(t == null ? '' : t).toLowerCase().replace(/œ/g, 'oe').replace(/æ/g, 'ae')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Mots utiles d'un texte (≥ 3 lettres, hors mots courants), au singulier grossier (s ou x final retiré), sans doublon. */
export function mots(t) {
  return uniques(normaliser(t).split(/[^a-z0-9]+/).filter((m) => m.length >= 3 && !VIDES.has(m))
    .map((m) => (m.length > 4 ? m.replace(/[sx]$/, '') : m))).slice(0, 12);
}

/** Nombre de mots demandés présents dans une recette (mot entier, ou début commun d'au moins 5 lettres). */
export function touche(demandes, presents) {
  if (!demandes || !demandes.length || !presents || !presents.length) return 0;
  return demandes.filter((m) => presents.some((w) => w === m
    || (Math.min(w.length, m.length) >= 5 && (w.startsWith(m) || m.startsWith(w))))).length;
}

/** Corps envoyé à la fonction « idee » : valeurs du vocabulaire seulement, texte borné. */
export function idee(rep, texte, conversation) {
  const choisies = uniques((rep.dures || []).concat(rep.souples || []));
  const contraintes = CONTRAINTES_ORDRE.filter((c) => choisies.includes(c));
  if (choisies.includes('recevoir')) contraintes.push('recevoir');
  return {
    v: 1, conversation,
    texte: String(texte == null ? '' : texte).replace(/\s+/g, ' ').trim().slice(0, TEXTE_MAX),
    envie: ENVIES.includes(rep.e) || rep.e === SURPRISE ? rep.e : null,
    duree: rang(rep.d) >= 0 ? rep.d : null,
    taille: Object.prototype.hasOwnProperty.call(TAILLES, rep.n) ? rep.n : null,
    contraintes,
  };
}

/** Identifiant de conversation de l'onglet (limites du serveur), aléatoire, jamais lié à une personne. */
export function conversation() {
  try {
    const v = sessionStorage.getItem(CLE_CONV);
    if (v && /^[A-Za-z0-9_-]{16,64}$/.test(v)) return v;
  } catch (err) { /* stockage indisponible */ }
  const b = new Uint8Array(18);
  (globalThis.crypto && crypto.getRandomValues ? crypto.getRandomValues(b) : b.forEach((x, i) => { b[i] = Math.floor(Math.random() * 256); }));
  const v = Array.from(b, (x) => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'[x & 63]).join('');
  try { sessionStorage.setItem(CLE_CONV, v); } catch (err) { /* stockage indisponible */ }
  return v;
}

function chaines(v) { return Array.isArray(v) && v.every((x) => typeof x === 'string') ? v.slice() : null; }
function uniques(v) { return Array.isArray(v) ? [...new Set(v.filter((x) => typeof x === 'string'))] : []; }
function comparer(a, b) { for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1; return 0; }

/** Entrée d'index utilisable, ou null. Une entrée douteuse est écartée, jamais réparée. */
export function entree(r) {
  if (!r || typeof r !== 'object') return null;
  const c = chaines(r.c);
  const s = r.s == null ? [] : chaines(r.s);
  const k = r.k == null ? [] : chaines(r.k);
  if (typeof r.h !== 'string' || !RE_H.test(r.h) || typeof r.u !== 'string' || !RE_U.test(r.u)
    || typeof r.t !== 'string' || !r.t.trim() || !ENVIES.includes(r.e) || rang(r.d) < 0
    || !c || !s || !k || !Number.isInteger(r.n)) return null;
  const i = typeof r.i === 'string' && RE_I.test(r.i) ? r.i : '';
  return {
    h: r.h, u: r.u, t: r.t, e: r.e, d: r.d, m: Number.isInteger(r.m) && r.m > 0 ? r.m : 0, c, s, k, n: r.n,
    o: r.o === 'testee' ? 'testee' : 'ia', g: Number.isFinite(r.g) ? Math.max(0, Math.min(100, Math.round(r.g))) : 0,
    i, il: i && LEGENDES.includes(r.il) ? r.il : '', nv: NIVEAUX.includes(r.nv) ? r.nv : '',
    w: mots(r.t + ' ' + (typeof r.w === 'string' ? r.w.slice(0, 600) : '')),
  };
}

/** Entrées valides de l'index, sans doublon de handle. */
export function entrees(index) {
  const vus = new Set();
  const out = [];
  for (const x of (index && Array.isArray(index.r) ? index.r : [])) {
    const y = entree(x);
    if (y && !vus.has(y.h)) { vus.add(y.h); out.push(y); }
  }
  return out;
}

/** Mélange de Fisher-Yates ; rnd() renvoie un nombre de [0, 1) (injectable dans les tests). */
export function melanger(liste, rnd) {
  const a = liste.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.min(i, Math.max(0, Math.floor(rnd() * (i + 1))));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Pages de `taille` recettes d'envies différentes quand c'est possible, sans changer l'ordre d'un palier à l'autre. */
export function diversifier(liste, taille = PAR_PAGE) {
  const reste = liste.slice();
  const out = [];
  while (reste.length) {
    const page = [];
    const vues = new Set();
    for (let i = 0; i < reste.length && page.length < taille; i++) {
      if (!vues.has(reste[i].e)) { vues.add(reste[i].e); page.push(reste.splice(i, 1)[0]); i--; }
    }
    while (page.length < taille && reste.length) page.push(reste.shift());
    out.push(...page);
  }
  return out;
}

/**
 * Sélection par règle (R-plan § 2.7, amendé par R-L0 A7).
 * rep = {e, d, dures[], souples[], deja[], mots[]} (n, la taille, ne filtre pas : elle met les portions à l'échelle).
 * mots (boîte à idées, 16/10) : les recettes qui contiennent le plus de mots demandés passent devant ; à rang égal, l'ordre
 * est tiré au sort (rnd) pour que 400 recettes ne donnent pas toujours les trois mêmes idées.
 * Renvoie {propositions (≤ 3), reserve, x, proches}. Les contraintes dures ne sont jamais relâchées ; une contrainte dure
 * inconnue est exigée telle quelle (aucune recette ne la porte : rien n'est proposé). Seul le temps peut être relâché,
 * d'un palier, et x vaut alors ['temps'].
 */
export function choisir(index, rep, rnd = Math.random) {
  const r = entrees(index);
  const q = rep || {};
  const e = q.e;
  let d = rang(q.d);
  if (d < 0) d = DUREES.length - 1;
  const dures = uniques(q.dures);
  const souples = uniques(q.souples).filter((s) => SOUPLES.includes(s));
  const deja = uniques(q.deja).filter((s) => DEJA.includes(s));
  const demandes = uniques(q.mots).slice(0, 12);
  const admis = (y) => y.n >= N_MIN && dures.every((c) => y.c.includes(c));
  const c0 = r.filter((y) => admis(y) && (e === SURPRISE || y.e === e));
  let x = [];
  let c1 = c0.filter((y) => rang(y.d) <= d);
  if (!c1.length && d < DUREES.length - 1) {
    c1 = c0.filter((y) => rang(y.d) === d + 1);
    if (c1.length) x = ['temps'];
  }
  const manque = (y) => souples.filter((s) => !y.c.includes(s) && !y.s.includes(s)).length;
  const score = (y) => -touche(demandes, y.w || []);
  const cle = (y) => [score(y), manque(y), y.o === 'testee' ? 0 : 1, d - rang(y.d), -y.k.filter((v) => deja.includes(v)).length, -Math.floor(y.g / 25), -y.n];
  const parCle = (a, b) => comparer(cle(a), cle(b)) || (a.h < b.h ? -1 : a.h > b.h ? 1 : 0);
  const parCleTirage = (a, b) => comparer(cle(a), cle(b)); // tri stable : l'ordre tiré au sort départage les égalités
  let ordre;
  if (e === SURPRISE) {
    // Tirage parmi les recettes de même rang grossier (souples, temps, « J'ai déjà »), puis envies variées dans chaque
    // palier. « J'ai déjà » reste un critère : le client l'a dit, le tirage ne l'ignore pas (R-plan § 1.3).
    const grossiere = (y) => [score(y), manque(y), d - rang(y.d), -y.k.filter((v) => deja.includes(v)).length];
    const tri = melanger(c1, rnd).sort((a, b) => comparer(grossiere(a), grossiere(b)));
    ordre = [];
    for (let i = 0; i < tri.length;) {
      let j = i;
      while (j < tri.length && comparer(grossiere(tri[j]), grossiere(tri[i])) === 0) j++;
      ordre.push(...diversifier(tri.slice(i, j)));
      i = j;
    }
  } else {
    ordre = melanger(c1, rnd).sort(parCleTirage);
  }
  const proches = ordre.length ? [] : r.filter((y) => admis(y) && rang(y.d) <= d).sort(parCle).slice(0, PAR_PAGE);
  return { propositions: ordre.slice(0, PAR_PAGE), reserve: ordre.slice(PAR_PAGE), x, proches };
}

/** Signature des contraintes d'une recette (FNV-1a 32 bits, base 36). Gardée dans l'historique à la place des réponses
    « Autre chose ? » : elle décrit la recette, pas le client. */
export function signature(c) {
  const s = uniques(c).sort().join(',');
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(36);
}

/** Liste d'idées pour l'historique : [[handle, signature], …]. */
export function memo(liste) { return (liste || []).map((y) => [y.h, signature(y.c)]); }

/** Idées mémorisées encore proposables, dans le même ordre : présentes dans l'index, n ≥ 2 et contraintes inchangées
    depuis qu'elles ont été montrées. Une recette modifiée entre-temps disparaît : jamais de contrainte relâchée,
    sans avoir à mémoriser les réponses du client. */
export function garder(tous, liste) {
  const parH = new Map((tous || []).map((y) => [y.h, y]));
  const vus = new Set();
  const out = [];
  for (const x of Array.isArray(liste) ? liste : []) {
    if (!Array.isArray(x) || typeof x[0] !== 'string' || typeof x[1] !== 'string' || vus.has(x[0])) continue;
    const y = parH.get(x[0]);
    if (y && y.n >= N_MIN && signature(y.c) === x[1]) { vus.add(y.h); out.push(y); }
  }
  return out;
}

/** Contraintes portées par toutes les recettes d'une liste (vide pour une liste vide). */
export function communes(liste) {
  if (!liste || !liste.length) return [];
  return liste[0].c.filter((c) => liste.every((y) => y.c.includes(c)));
}

/** Portions de la fiche pour une taille de l'arbre (t2 → 2…), sinon les portions de base. */
export function portions(n) { return TAILLES[n] || PORTIONS_BASE; }

/** Adresse de la fiche : celle de l'index (déjà dans la langue de la page) + #p=N (R-plan § 2.4). */
export function lien(y, n) { return y.u + '#p=' + portions(n); }

/** Passage vers la fiche (R-plan § 2.4, R-L0 A8) : ni contrainte de santé, ni texte. */
export function passage(rep, h, x, maintenant) {
  const p = { h, deja: uniques(rep.deja).filter((s) => DEJA.includes(s)), t: maintenant };
  if (x && x.includes('temps')) p.x = ['temps'];
  if (uniques(rep.dures).includes('enfants')) p.ctx = ['enfants'];
  return p;
}

/** Réponses mémorisées {e, n, d} (localStorage), validées ; un objet vide sinon. */
export function lireMemoire(texte) {
  let o = null;
  try { o = JSON.parse(texte || 'null'); } catch (err) { o = null; }
  if (!o || typeof o !== 'object') return {};
  const out = {};
  if (ENVIES.includes(o.e) || o.e === SURPRISE) out.e = o.e;
  if (Object.prototype.hasOwnProperty.call(TAILLES, o.n)) out.n = o.n;
  if (rang(o.d) >= 0) out.d = o.d;
  return out;
}

/** Textes de la configuration : le filtre t de Shopify peut échapper « & » (« Pizza &amp; focaccia ») ; posés par
    textContent, ils sont donc d'abord décodés (sans effet sur un texte non échappé). */
export function decoder(v) {
  if (typeof v === 'string') {
    return v.replace(/&(amp|lt|gt|quot|#39|#x27);/g, (m, k) => ({ amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", '#x27': "'" })[k]);
  }
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = {};
    Object.keys(v).forEach((k) => { o[k] = decoder(v[k]); });
    return o;
  }
  return v;
}

/** Remplace __N__, __A__… dans un texte de la configuration. */
export function fmt(s, v) {
  return String(s == null ? '' : s).replace(/__([A-Z])__/g, (m, k) => (v && v[k] != null ? String(v[k]) : m));
}

/* ------------------------------------------------------------------ Interface (navigateur seulement) */

// ?choix= (assistant, action lancer_recettes ; intégration du 9 octobre) : une envie présélectionne l'envie, une
// contrainte coche sa pastille (mémoire de la page seulement), « regimes » ouvre l'écran des régimes ; une valeur
// inconnue est ignorée. Sans gluten, sans lactose et sans porc ne passent jamais par l'adresse (R-decisions, point 8 :
// rien qui révèle une donnée de santé ou une conviction) : ils ouvrent l'écran des régimes sans rien cocher.
// Le paramètre est retiré de l'adresse dès sa lecture (historique du navigateur).
export const CHOIX_SENSIBLES = ['sans_gluten', 'sans_lactose', 'sans_porc'];
export function lireChoix(search) {
  let v = '';
  try { v = String(new URLSearchParams(search || '').get('choix') || '').trim().toLowerCase(); } catch (err) { return null; }
  if (!v) return null;
  if (ENVIES.includes(v) || v === SURPRISE) return { e: v };
  if (v === 'regimes' || CHOIX_SENSIBLES.includes(v)) return { ecran: 'regimes' };
  if (CONTRAINTES_DURES.includes(v)) return { dure: v };
  if (SOUPLES.includes(v)) return { souple: v };
  return null;
}
export function sansChoix(pathname, search, hash) {
  let q = '';
  try { const u = new URLSearchParams(search || ''); u.delete('choix'); q = u.toString(); } catch (err) { q = ''; }
  return (pathname || '') + (q ? '?' + q : '') + (hash || '');
}

const ETAPES = ['envie', 'temps', 'combien', 'regimes'];
const SUIVANT = { e: 'temps', d: 'combien', n: 'regimes' };
const PRECEDENT = { temps: 'envie', combien: 'temps', regimes: 'combien', idees: 'regimes', aucune: 'regimes' };
// Version de l'état d'historique. La v1 (première livraison de L2) y gardait les pastilles : elle est ignorée.
export const V_ETAT = 2;

function vide() { return { e: null, d: null, n: null, dures: [], souples: [], deja: [], mots: [] }; }

function init(root) {
  if (root.dataset.rciaPret) return;
  root.dataset.rciaPret = '1';
  const json = (sel) => { try { return JSON.parse(root.querySelector(sel).textContent); } catch (err) { return null; } };
  const index = json('[data-rcia-index]') || { v: 1, r: [] };
  const cfg = json('[data-rcia-config]') || {};
  const T = decoder(cfg.t || {});
  let tous = entrees(index);
  const ecrans = {};
  root.querySelectorAll('[data-rcia-ecran]').forEach((el) => { ecrans[el.dataset.rciaEcran] = el; });
  const prog = root.querySelector('[data-rcia-prog]');
  const etape = root.querySelector('[data-rcia-etape]');
  const jauge = root.querySelector('[data-rcia-jauge]');
  const tpl = root.querySelector('template[data-rcia-carte]');
  const voir = root.querySelector('[data-rcia-voir]');
  const dejaZone = root.querySelector('[data-rcia-deja-zone]');
  const dejaBtn = root.querySelector('[data-rcia-deja-ouvrir]');
  const libre = root.querySelector('[data-rcia-libre]');
  const txt = libre && libre.querySelector('[data-rcia-txt]');
  let ideeEnvoyee = false; // boîte à idées : merci affiché sur les idées qui suivent l'envoi
  const toutes = () => document.getElementById(root.dataset.rciaToutes || '');
  const oublierPassage = () => { try { sessionStorage.removeItem(CLE_PASSAGE); } catch (err) { /* stockage indisponible */ } };
  // Un passage resté d'un clic sans navigation (retour avant le chargement de la fiche) ne doit pas traîner.
  oublierPassage();

  // Envies sans recette proposable : tuile masquée (pas d'impasse en 4 gestes). Aucune : l'arbre se retire.
  const envies = new Set(tous.filter((y) => y.n >= N_MIN).map((y) => y.e));
  if (!envies.size) {
    root.hidden = true;
    const t = toutes();
    if (t) t.open = true;
    return;
  }
  root.querySelectorAll('button[data-rcia-rep="e"]').forEach((b) => { if (b.value !== SURPRISE && !envies.has(b.value)) b.hidden = true; });

  // Pages suivantes de l'index (plus de 250 recettes, 16/10) : lues en arrière-plan ; le calcul les attend 3 s au plus.
  const suite = completer();
  function completer() {
    const pages = Math.min(8, Number(root.dataset.rciaPages) || 1);
    const sec = root.dataset.rciaSection;
    if (pages < 2 || !sec || typeof fetch !== 'function' || typeof DOMParser !== 'function') return Promise.resolve();
    const lire = (p) => fetch(location.pathname + '?section_id=' + encodeURIComponent(sec) + '&page=' + p, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const s = html && new DOMParser().parseFromString(html, 'text/html').querySelector('script[data-rcia-index-suite]');
        try { return s ? JSON.parse(s.textContent) : null; } catch (err) { return null; }
      })
      .catch(() => null);
    const liste = [];
    for (let p = 2; p <= pages; p++) liste.push(lire(p));
    return Promise.all(liste).then((res) => {
      res.forEach((x) => { if (x && Array.isArray(x.r)) index.r = index.r.concat(x.r); });
      tous = entrees(index);
      tous.filter((y) => y.n >= N_MIN).forEach((y) => envies.add(y.e));
      root.querySelectorAll('button[data-rcia-rep="e"]').forEach((b) => { if (b.value === SURPRISE || envies.has(b.value)) b.hidden = false; });
    });
  }
  const attendre = () => Promise.race([suite, new Promise((ok) => setTimeout(ok, 3000))]);

  let rep = vide();
  // cm : contraintes communes des idées restaurées depuis l'historique (les pastilles n'y sont pas) ; null sinon.
  let vue = { ordre: [], off: 0, x: [], proches: [], cm: null };
  let courant = 'envie';
  const memoire = (() => { try { return lireMemoire(localStorage.getItem(CLE_LOCAL)); } catch (err) { return {}; } })();
  const manquante = () => (!rep.e ? 'envie' : !rep.d ? 'temps' : !rep.n ? 'combien' : 'regimes');

  function presser() {
    root.querySelectorAll('button[data-rcia-rep]').forEach((b) => {
      const k = b.dataset.rciaRep;
      const val = rep[k] != null ? rep[k] : memoire[k];
      b.setAttribute('aria-pressed', String(b.value === val));
    });
    root.querySelectorAll('button[data-rcia-chip]').forEach((b) => {
      const g = { dure: rep.dures, souple: rep.souples, deja: rep.deja }[b.dataset.rciaChip] || [];
      b.setAttribute('aria-pressed', String(g.includes(b.value)));
    });
    const choix = rep.dures.length + rep.souples.length + rep.deja.length + (txt && txt.value.trim().length >= TEXTE_MIN ? 1 : 0);
    if (voir) voir.textContent = decoder(choix ? voir.dataset.lVoir : voir.dataset.lPasser);
    if (dejaZone && rep.deja.length) { dejaZone.hidden = false; if (dejaBtn) dejaBtn.setAttribute('aria-expanded', 'true'); }
    majLibre();
  }

  function carte(y) {
    const li = tpl.content.firstElementChild.cloneNode(true);
    const a = li.querySelector('a');
    a.href = lien(y, rep.n);
    a.dataset.rciaH = y.h;
    const img = li.querySelector('img');
    const sur = (u) => typeof u === 'string' && (RE_I.test(u) || RE_U.test(u));
    const repli = (cfg.img || {})[y.e]; // illustration de l'envie, émise par la section
    let src = y.i;
    let leg = '';
    if (src) leg = y.il === 'ia' ? T.image_ia : y.il === 'produit' ? T.photo_ingredient : '';
    else { src = repli; li.classList.add('eg-rcia-ill'); }
    if (img && sur(src)) {
      img.src = src;
      if (y.i && sur(repli)) {
        // Photo introuvable (supprimée, adresse périmée) : l'illustration de l'envie, sans légende, plutôt qu'une icône cassée.
        img.addEventListener('error', () => {
          img.src = repli;
          li.classList.add('eg-rcia-ill');
          const l = li.querySelector('[data-c="l"]');
          if (l) l.remove();
        }, { once: true });
      }
    } else if (img) img.remove();
    const slot = (k) => li.querySelector('[data-c="' + k + '"]');
    slot('t').textContent = y.t;
    const meta = [];
    if (y.m) meta.push(fmt(T.minutes, { N: y.m }));
    if (y.nv && T.niveau && T.niveau[y.nv]) meta.push(T.niveau[y.nv]);
    slot('m').textContent = meta.join(' · ');
    const badges = slot('b');
    const badge = (texte, cls) => { const s = document.createElement('span'); s.className = 'badge ' + cls; s.textContent = texte; badges.appendChild(s); };
    if (y.o === 'testee' && T.testee) badge(T.testee, 'b-pack');
    const lab = T.etiquette || {};
    // Demandées : les pastilles de la page, ou, après une restauration, les contraintes communes des idées montrées.
    const dem = new Set(rep.dures.concat(rep.souples, vue.cm || []));
    CONTRAINTES_ORDRE.forEach((c) => {
      const voulu = dem.has(c) || c === 'vegetarien' || c === 'petit_budget';
      if (voulu && y.c.includes(c) && lab[c]) badge(lab[c], 'b-lab');
    });
    if (dem.has('recevoir') && y.s.includes('recevoir') && lab.recevoir) badge(lab.recevoir, 'b-lab');
    if (!badges.childNodes.length) badges.remove();
    if (y.g > 0 && T.part) slot('p').textContent = fmt(T.part, { N: y.g }); else slot('p').remove();
    if (leg) slot('l').textContent = leg; else slot('l').remove();
    return li;
  }

  function remplir(ul, liste) {
    ul.textContent = '';
    liste.forEach((y) => ul.appendChild(carte(y)));
  }

  // Texte saisi : le bouton devient « Voir mes recettes » (l'idée part avec).
  function majLibre() {
    if (voir) voir.textContent = decoder(rep.dures.length + rep.souples.length + rep.deja.length
      || (txt && txt.value.trim().length >= TEXTE_MIN) ? voir.dataset.lVoir : voir.dataset.lPasser);
  }
  if (txt) txt.addEventListener('input', majLibre);

  // Envoi de l'idée sans attendre (keepalive : il aboutit même si le visiteur ouvre aussitôt une fiche). Le merci n'est
  // montré qu'après la réponse du serveur ; une erreur (limite, panne) ne gêne pas le visiteur.
  function envoyerIdee(texte) {
    if (!libre || !libre.dataset.url || typeof fetch !== 'function') return;
    fetch(libre.dataset.url, {
      method: 'POST', credentials: 'omit', keepalive: true, headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(idee(rep, texte, conversation())),
    }).then((r) => {
      if (!r.ok) return;
      ideeEnvoyee = true;
      if (courant === 'idees' || courant === 'aucune') rendre();
    }).catch(() => { /* réseau : l'idée est perdue, la page continue */ });
  }

  function rendre() {
    const merci = ecrans[courant] && ecrans[courant].querySelector('[data-rcia-merci]');
    if (merci) merci.hidden = !ideeEnvoyee;
    if (courant === 'idees') {
      const el = ecrans.idees;
      const total = vue.ordre.length;
      const page = vue.ordre.slice(vue.off, vue.off + PAR_PAGE);
      remplir(el.querySelector('[data-rcia-liste]'), page);
      const lab = [T.envie && T.envie[rep.e], T.duree && T.duree[rep.d]]
        .concat(rep.dures.map((c) => T.contrainte && T.contrainte[c]), rep.souples.map((c) => T.contrainte && T.contrainte[c]))
        .filter(Boolean);
      el.querySelector('[data-rcia-resume]').textContent = lab.join(' · ');
      el.querySelector('[data-rcia-note-temps]').hidden = !vue.x.includes('temps');
      el.querySelector('[data-rcia-autre]').hidden = total <= PAR_PAGE;
      el.querySelector('[data-rcia-compte]').textContent = total > PAR_PAGE
        ? fmt(T.compte, { A: vue.off + 1, B: Math.min(vue.off + PAR_PAGE, total), T: total }) : '';
    } else if (courant === 'aucune') {
      const el = ecrans.aucune;
      remplir(el.querySelector('[data-rcia-proches]'), vue.proches || []);
      el.querySelector('[data-rcia-proches-titre]').hidden = !(vue.proches || []).length;
    }
  }

  function montrer(nom, focus) {
    courant = nom;
    Object.keys(ecrans).forEach((k) => { ecrans[k].hidden = k !== nom; });
    const i = ETAPES.indexOf(nom);
    if (prog) prog.hidden = i < 0;
    if (i >= 0) {
      if (etape) etape.textContent = fmt(T.etape, { N: i + 1 });
      if (jauge) jauge.style.width = ((i + 1) * 100 / ETAPES.length) + '%';
    }
    presser();
    rendre();
    if (focus) {
      const h = ecrans[nom] && ecrans[nom].querySelector('h2');
      if (root.getBoundingClientRect().top < 0) root.scrollIntoView({ block: 'start' });
      if (h) h.focus({ preventScroll: true });
    }
  }

  // État de l'entrée d'historique : pos (rang depuis l'arrivée sur la page), prec (écran d'où l'on vient), {e, n, d},
  // et les idées montrées sous forme [handle, signature des contraintes de la recette]. Aucune pastille.
  function etat(pos, prec) {
    return { rcia: { v: V_ETAT, pos, prec, ecran: courant, rep: { e: rep.e, n: rep.n, d: rep.d },
      vue: { ordre: memo(vue.ordre), off: vue.off, x: vue.x, proches: memo(vue.proches) } } };
  }
  function lu() { const s = history.state && history.state.rcia; return s && s.v === V_ETAT ? s : {}; }
  function pos() { return Number.isInteger(lu().pos) ? lu().pos : 0; }
  function remplacer(url) { try { history.replaceState(etat(pos(), lu().prec), '', url); } catch (err) { /* historique indisponible */ } }
  function aller(nom) {
    const p = pos();
    const prec = courant;
    montrer(nom, true);
    try { history.pushState(etat(p + 1, prec), ''); } catch (err) { /* historique indisponible */ }
  }

  function calculer() {
    const res = choisir({ v: 1, r: tous }, rep);
    vue = { ordre: res.propositions.concat(res.reserve), off: 0, x: res.x, proches: res.proches, cm: null };
    try { localStorage.setItem(CLE_LOCAL, JSON.stringify({ e: rep.e, n: rep.n, d: rep.d })); } catch (err) { /* stockage indisponible */ }
    return vue.ordre.length ? 'idees' : 'aucune';
  }

  // Les pastilles ne sont pas dans l'historique : un retour dans la page garde celles de sa mémoire ; après un
  // rechargement ou un retour depuis la fiche (sans cache de page), elles sont vides.
  function restaurer(s, focus) {
    const m = lireMemoire(JSON.stringify(s.rep || {}));
    rep.e = m.e || null;
    rep.n = m.n || null;
    rep.d = m.d || null;
    let nom = Object.prototype.hasOwnProperty.call(ecrans, s.ecran) ? s.ecran : 'envie';
    if (nom === 'idees' || nom === 'aucune') {
      const sv = s.vue || {};
      // Les idées déjà montrées, dans le même ordre ; une recette retirée ou modifiée depuis disparaît (garder).
      const ordre = nom === 'idees' ? garder(tous, sv.ordre) : [];
      const proches = nom === 'aucune' ? garder(tous, sv.proches).slice(0, PAR_PAGE) : [];
      if (nom === 'idees' && !ordre.length) {
        // Plus aucune des idées montrées : jamais de nouveau calcul sans les régimes, qui ne sont pas gardés ; on les redemande.
        vue = { ordre: [], off: 0, x: [], proches: [], cm: null };
        nom = manquante();
      } else {
        const off = Number.isInteger(sv.off) && sv.off >= 0 && sv.off < ordre.length ? sv.off : 0;
        vue = { ordre, off: off - (off % PAR_PAGE), x: (Array.isArray(sv.x) ? sv.x : []).filter((v) => v === 'temps'),
          proches, cm: communes(ordre.length ? ordre : proches) };
      }
    }
    montrer(nom, focus);
  }

  root.addEventListener('click', (ev) => {
    const t = ev.target.closest('button, a');
    if (!t || !root.contains(t)) return;
    if (t.matches('button[data-rcia-rep]')) {
      const k = t.dataset.rciaRep;
      rep[k] = t.value;
      aller(SUIVANT[k]);
    } else if (t.matches('button[data-rcia-chip]')) {
      const g = { dure: 'dures', souple: 'souples', deja: 'deja' }[t.dataset.rciaChip];
      if (!g) return;
      const on = !rep[g].includes(t.value);
      rep[g] = on ? rep[g].concat(t.value) : rep[g].filter((v) => v !== t.value);
      presser(); // mémoire de la page seulement (rien dans l'historique)
    } else if (t.matches('[data-rcia-deja-ouvrir]')) {
      const ouvert = t.getAttribute('aria-expanded') === 'true';
      t.setAttribute('aria-expanded', String(!ouvert));
      if (dejaZone) dejaZone.hidden = ouvert;
    } else if (t.matches('[data-rcia-voir]')) {
      if (!rep.e || !rep.d || !rep.n) { aller(manquante()); return; }
      const texte = txt ? txt.value.replace(/\s+/g, ' ').trim() : '';
      rep.mots = texte.length >= TEXTE_MIN ? mots(texte) : [];
      ideeEnvoyee = false;
      if (texte.length >= TEXTE_MIN) {
        envoyerIdee(texte);
        txt.value = ''; // une idée n'est envoyée qu'une fois
      }
      t.setAttribute('aria-busy', 'true');
      attendre().then(() => { t.removeAttribute('aria-busy'); aller(calculer()); });
    } else if (t.matches('[data-rcia-autre]')) {
      const total = vue.ordre.length;
      vue.off = vue.off + PAR_PAGE >= total ? 0 : vue.off + PAR_PAGE;
      rendre();
      remplacer();
    } else if (t.matches('[data-rcia-modifier]')) {
      aller('regimes');
      if (txt && !txt.hidden) txt.focus({ preventScroll: true });
    } else if (t.matches('[data-rcia-recommencer]')) {
      rep = vide();
      ideeEnvoyee = false;
      vue = { ordre: [], off: 0, x: [], proches: [], cm: null };
      aller('envie');
    } else if (t.matches('[data-rcia-toutes-voir]')) {
      const d = toutes();
      if (d) {
        d.open = true;
        d.scrollIntoView({ block: 'start' });
        const s = d.querySelector('summary');
        if (s) s.focus({ preventScroll: true });
      }
    } else if (t.matches('[data-rcia-retour]')) {
      // « Retour » = la question précédente. L'historique n'est remonté que s'il y mène (sinon, après « Modifier mes
      // réponses », il ramènerait aux idées).
      const cible = PRECEDENT[courant] || 'envie';
      if (pos() > 0 && lu().prec === cible) history.back();
      else aller(cible);
    } else if (t.matches('a[data-rcia-h]')) {
      // Pas de preventDefault : le lien reste un vrai lien. Ouverture dans un autre onglet (touche de modification) :
      // pas de passage, la fiche s'ouvre comme une visite directe.
      if (ev.button !== 0 || ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey) return;
      // « Pour des enfants » (A8, accompagnements sans alcool) suit aussi après une restauration (contraintes communes).
      const r = { deja: rep.deja, dures: rep.dures.concat(vue.cm || []) };
      try { sessionStorage.setItem(CLE_PASSAGE, JSON.stringify(passage(r, t.dataset.rciaH, vue.x, Date.now()))); } catch (err) { /* stockage indisponible */ }
    }
  });

  // Audit du 9/10 (J15) : écouteurs de window retirés quand l'éditeur de thème recharge la section (pas de cumul).
  const fin = typeof AbortController === 'function' ? new AbortController() : null;
  const sig = fin ? { signal: fin.signal } : undefined;
  root.__egRciaFin = () => { if (fin) fin.abort(); };
  window.addEventListener('popstate', (ev) => {
    const s = ev.state && ev.state.rcia;
    if (s && s.v === V_ETAT) restaurer(s, true);
    // Entrée sans état de l'arbre (ancre de la page, ancienne version) : l'arbre reste tel quel.
  }, sig);
  window.addEventListener('pageshow', (ev) => { if (ev.persisted) oublierPassage(); }, sig);

  const s0 = lu();
  const aChoix = (() => { try { return new URLSearchParams(location.search).has('choix'); } catch (err) { return false; } })();
  const ch = aChoix ? lireChoix(location.search) : null;
  const propre = () => sansChoix(location.pathname, location.search, location.hash);
  if (s0.v === V_ETAT) {
    // Retour depuis la fiche (« Retour aux idées ») ou rechargement ; les idées gardées peuvent venir des pages suivantes.
    if ((s0.ecran === 'idees' || s0.ecran === 'aucune') && Number(root.dataset.rciaPages) > 1) attendre().then(() => restaurer(lu().v === V_ETAT ? lu() : s0, false));
    else restaurer(s0, false);
    if (aChoix) remplacer(propre());
  } else if (ch && ch.e && (ch.e === SURPRISE || envies.has(ch.e))) {
    rep.e = ch.e;
    montrer('temps', true);
    remplacer(propre());
  } else if (ch && ch.ecran === 'regimes') {
    // Réponses 1 à 3 reprises de la mémoire si elle existe ; « Voir mes recettes » mène sinon à la question manquante.
    Object.assign(rep, memoire);
    montrer('regimes', true);
    remplacer(propre());
  } else if (ch && (ch.dure || ch.souple)) {
    if (ch.dure) rep.dures = [ch.dure];
    else rep.souples = [ch.souple];
    montrer('envie', true);
    remplacer(propre());
  } else if (aChoix) {
    montrer('envie', false);
    remplacer(propre());
  } else if (location.hash === '#ecran=4') {
    // « Modifier mes réponses » depuis la fiche : écran des régimes, réponses 1 à 3 reprises de la mémoire, pastilles vides.
    Object.assign(rep, memoire);
    montrer(manquante(), true);
    remplacer(location.pathname + location.search);
  } else {
    montrer('envie', false);
    remplacer();
  }
}

if (typeof document !== 'undefined') {
  const lancer = (el) => el.querySelectorAll('[data-rcia-arbre]').forEach(init);
  lancer(document);
  document.addEventListener('shopify:section:load', (ev) => lancer(ev.target));
  document.addEventListener('shopify:section:unload', (ev) => {
    if (ev.target && ev.target.querySelectorAll) ev.target.querySelectorAll('[data-rcia-arbre]').forEach((r) => { if (r.__egRciaFin) r.__egRciaFin(); });
  });
}
